/* PREYSIGHT — Lake Day: first-person lure fishing (Three.js, mobile first). */
import * as THREE from "https://www.preysight.com/cdn/shop/t/29/assets/three.module.min.js?v=56722694035991050461790942115";
import { Water } from "https://www.preysight.com/cdn/shop/t/29/assets/sg-three-water.js?v=106618797507648771131791276913";

const WATER_NORMALS = "https://www.preysight.com/cdn/shop/t/29/assets/sg-waternormals.jpg?v=111222937578257039781790942115";

/* ---------------- data ---------------- */
const SPECIES = {
  perch: { name: 'Perch', min: 0.3, max: 1.9, len: [0.2, 0.4], girth: 0.27, back: 0x46542a, belly: 0xdcd39a, fin: 0xe0662a, pattern: 'bars', aggr: 1.0, style: 'twitch', power: 0.55, jump: 0.0, weight: 4 },
  bass: { name: 'Largemouth Bass', min: 1, max: 6.8, len: [0.3, 0.62], girth: 0.27, back: 0x2c3d22, belly: 0xdfe2b8, fin: 0x55653a, pattern: 'band', aggr: 0.85, style: 'twitch', power: 0.85, jump: 0.55, weight: 3 },
  trout: { name: 'Rainbow Trout', min: 0.8, max: 7.2, len: [0.32, 0.68], girth: 0.22, back: 0x4f5e4c, belly: 0xefebe0, fin: 0x8b8e7c, pattern: 'trout', aggr: 0.75, style: 'steady', power: 0.9, jump: 0.7, weight: 2.2 },
  pike: { name: 'Northern Pike', min: 3, max: 22, len: [0.62, 1.15], girth: 0.17, back: 0x33421e, belly: 0xe0dba8, fin: 0x7d6c34, pattern: 'pike', aggr: 0.6, style: 'fast', power: 1.05, jump: 0.18, weight: 1.3 }
};
const STYLE_TIP = {
  twitch: 'Short pulls and pauses — it hits on the pause.',
  steady: 'Keep a steady retrieve. Don\'t stop.',
  fast: 'Keep it moving. Pike hate a lazy lure.'
};

const rand = (a, b) => a + Math.random() * (b - a);
const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const lerp = (a, b, t) => a + (b - a) * t;
const smooth = (a, b, x) => { const t = clamp((x - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); };
const fmtTime = (s) => { s = Math.max(0, Math.ceil(s)); return Math.floor(s / 60) + ':' + ('0' + (s % 60)).slice(-2); };
function pick(map) { let total = 0; for (const k in map) total += map[k].weight; let r = Math.random() * total; for (const k in map) { r -= map[k].weight; if (r <= 0) return k; } return 'perch'; }
function hash(x, y) { const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453; return s - Math.floor(s); }
function vnoise(x, y) {
  const xi = Math.floor(x), yi = Math.floor(y), xf = x - xi, yf = y - yi;
  const u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf);
  const a = hash(xi, yi), b = hash(xi + 1, yi), c = hash(xi, yi + 1), d = hash(xi + 1, yi + 1);
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
}
function fbm(x, y) { let s = 0, a = 0.5, f = 1; for (let i = 0; i < 4; i++) { s += a * vnoise(x * f, y * f); f *= 2; a *= 0.5; } return s; }
function store(key, val) { try { if (val === undefined) return window.localStorage.getItem(key); window.localStorage.setItem(key, val); } catch (e) {} return null; }

/* lake shape */
const lakeR = (th) => 48 + 7 * Math.sin(3 * th + 1.1) + 4.5 * Math.sin(5 * th + 0.4) + 2.4 * Math.sin(8 * th + 2.3);
function heightAt(x, z) {
  const r = Math.hypot(x, z), th = Math.atan2(z, x), d = r - lakeR(th);
  if (d < 0) return -0.8 + Math.max(-7, d * 0.22);
  const shore = Math.min(d * 0.22, 2.2);
  const hills = fbm(x * 0.005 + 3.1, z * 0.005 - 1.7) * 60 * smooth(18, 240, d);
  const bumps = (fbm(x * 0.04, z * 0.04) - 0.45) * 2.4 * smooth(4, 25, d);
  return -0.8 + shore + hills + bumps;
}
const ISLE = { x: 17, z: -12, r: 7.5 };
function insideLake(x, z, k) {
  if (Math.hypot(x - ISLE.x, z - ISLE.z) < ISLE.r + 2.5) return false;
  const th = Math.atan2(z, x); return Math.hypot(x, z) < lakeR(th) * (k || 0.92);
}

function instanced(geo, list, mat) {
  const m = new THREE.InstancedMesh(geo, mat || new THREE.MeshLambertMaterial({ color: 0xffffff }), list.length);
  const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), p = new THREE.Vector3(), s = new THREE.Vector3(1, 1, 1), e = new THREE.Euler(), c = new THREE.Color();
  list.forEach((it, i) => { p.set(it[0], it[1], it[2]); q.setFromEuler(e.set(0, it[3] || 0, 0)); m4.compose(p, q, s); m.setMatrixAt(i, m4); c.setHex(it[4] || 0xffffff); m.setColorAt(i, c); });
  m.instanceMatrix.needsUpdate = true; if (m.instanceColor) m.instanceColor.needsUpdate = true;
  return m;
}

/* ---------------- game ---------------- */
class LakeGame {
  constructor(root) {
    this.root = root;
    this.cv = root.querySelector('canvas');
    this.q = (s) => root.querySelector(s);
    this.cfg = {
      round: parseInt(root.dataset.round, 10) || 120,
      threshold: parseFloat(root.dataset.threshold) || 15,
      code: root.dataset.code || ''
    };
    this.mobile = (window.matchMedia && window.matchMedia('(hover: none)').matches) || Math.min(window.innerWidth, window.innerHeight) < 700;
    this.state = 'loading';
    this.time = 0; this.left = this.cfg.round;
    this.score = 0; this.count = 0; this.biggest = null;
    this.best = parseFloat(store('sg-lake-best') || '0') || 0;
    this.yaw = 0; this.pitch = -0.07;
    this.holding = false; this.charging = false; this.power = 0; this.powerT = 0;
    this.lure = { state: 'idle', pos: new THREE.Vector3(), from: new THREE.Vector3(), to: new THREE.Vector3(), t: 0, dur: 1, moving: false, moveT: 0, pauseT: 0 };
    this.follower = null; this.bite = null; this.fight = null; this.trophy = null;
    this.rings = []; this.lastHud = {};
    this.visible = true;
    this.tmp = new THREE.Vector3(); this.tmp2 = new THREE.Vector3();

    try {
      this.initRenderer();
      this.buildWorld();
      this.buildRod();
      this.buildFx();
      this.buildExtras();
    } catch (err) {
      this.fail(err);
      return;
    }
    this.populate();
    this.bind();
    this.resize();
    if ('ResizeObserver' in window) new ResizeObserver(() => this.resize()).observe(this.cv);
    if ('IntersectionObserver' in window) new IntersectionObserver((es) => { this.visible = es[0].isIntersecting; }, { threshold: 0.1 }).observe(root);
    this.last = performance.now();
    const loop = (now) => {
      requestAnimationFrame(loop);
      const dt = Math.min(0.05, (now - this.last) / 1000);
      this.last = now;
      if (!this.visible || document.hidden) return;
      try { this.update(dt); this.renderer.render(this.scene, this.camera); }
      catch (err) { if ((this.errs = (this.errs || 0) + 1) < 4) console.error('[sg-lake]', err); }
    };
    requestAnimationFrame(loop);
    this.state = 'intro';
    root.classList.add('is-ready');
    root.querySelectorAll('[data-g-start]').forEach((b) => { b.disabled = false; });
    const lbl = this.q('[data-g-start-label]'); if (lbl) lbl.textContent = 'Start fishing';
  }

  fail(err) {
    console.error('[sg-lake]', err);
    const lbl = this.q('[data-g-start-label]');
    if (lbl) lbl.textContent = 'Your browser can\'t run the game';
  }

  /* ---------- setup ---------- */
  initRenderer() {
    const r = new THREE.WebGLRenderer({ canvas: this.cv, antialias: !this.mobile, powerPreference: 'high-performance' });
    r.setPixelRatio(Math.min(window.devicePixelRatio || 1, this.mobile ? 1.5 : 2));
    r.toneMapping = THREE.ACESFilmicToneMapping;
    r.toneMappingExposure = 0.85;
    this.renderer = r;
    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.Fog(0xc8dbe6, 260, 1900);
    this.camera = new THREE.PerspectiveCamera(60, 1, 0.05, 20000);
    this.camera.rotation.order = 'YXZ';
    this.scene.add(this.camera);
    this.shoreZ = lakeR(Math.PI / 2) + 3.6;
    this.camPos = new THREE.Vector3(0, 2.2, this.shoreZ - 12);
    this.camera.position.copy(this.camPos);
  }

  buildWorld() {
    const scene = this.scene, mobile = this.mobile;
    // sun + sky
    this.sun = new THREE.Vector3(-0.42, 0.3, -0.86).normalize();
    const sky = new THREE.Mesh(new THREE.SphereGeometry(8000, 32, 16), new THREE.ShaderMaterial({
      side: THREE.BackSide, depthWrite: false, fog: false, toneMapped: false,
      uniforms: {
        top: { value: new THREE.Color(0x2f74c0) }, mid: { value: new THREE.Color(0x86b6df) },
        low: { value: new THREE.Color(0xd6e5ee) }, sunDir: { value: this.sun.clone() }
      },
      vertexShader: 'varying vec3 vDir; void main(){ vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
      fragmentShader: [
        'uniform vec3 top; uniform vec3 mid; uniform vec3 low; uniform vec3 sunDir; varying vec3 vDir;',
        'void main(){',
        '  vec3 d = normalize(vDir); float h = d.y;',
        '  vec3 c = mix(mid, top, smoothstep(0.02, 0.55, h));',
        '  c = mix(low, c, smoothstep(-0.03, 0.16, h));',
        '  float s = max(dot(d, normalize(sunDir)), 0.0);',
        '  c += vec3(1.0, 0.93, 0.78) * (pow(s, 2500.0) * 2.4 + pow(s, 60.0) * 0.22 + pow(s, 6.0) * 0.06);',
        '  gl_FragColor = vec4(c, 1.0);',
        '  #include <colorspace_fragment>',
        '}'
      ].join('\n')
    }));
    sky.renderOrder = -1;
    scene.add(sky);

    scene.add(new THREE.HemisphereLight(0xcfe3f2, 0x4a5530, 1.5));
    const dl = new THREE.DirectionalLight(0xfff1dc, 2.8);
    dl.position.copy(this.sun).multiplyScalar(200);
    scene.add(dl);

    // clouds
    const cc = document.createElement('canvas'); cc.width = 256; cc.height = 128;
    const cx = cc.getContext('2d');
    for (let i = 0; i < 14; i++) {
      const x = rand(50, 206), y = rand(50, 86), rr = rand(22, 44);
      const g = cx.createRadialGradient(x, y, 0, x, y, rr);
      g.addColorStop(0, 'rgba(255,255,255,0.9)'); g.addColorStop(1, 'rgba(255,255,255,0)');
      cx.fillStyle = g; cx.fillRect(0, 0, 256, 128);
    }
    const ctex = new THREE.CanvasTexture(cc); ctex.colorSpace = THREE.SRGBColorSpace;
    for (let i = 0; i < 7; i++) {
      const m = new THREE.Sprite(new THREE.SpriteMaterial({ map: ctex, transparent: true, opacity: rand(0.45, 0.8), fog: false, depthWrite: false }));
      const a = rand(-1.4, 1.4), dist = rand(1400, 2400);
      m.position.set(Math.sin(a) * dist, rand(220, 520), -Math.cos(a) * dist + this.camPos.z);
      m.scale.set(rand(700, 1300), rand(220, 380), 1);
      scene.add(m);
    }

    // water
    const tex = new THREE.TextureLoader().load(WATER_NORMALS, (t) => { t.wrapS = t.wrapT = THREE.RepeatWrapping; });
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    this.water = new Water(new THREE.PlaneGeometry(3000, 3000), {
      textureWidth: mobile ? 384 : 768, textureHeight: mobile ? 384 : 768,
      waterNormals: tex, sunDirection: this.sun.clone(), sunColor: 0xfff4e0, waterColor: 0x0a2430,
      distortionScale: 1.6, fog: true
    });
    this.water.rotation.x = -Math.PI / 2;
    this.water.material.uniforms.size.value = 3.2;
    scene.add(this.water);

    // terrain: polar grid, dense around the shoreline
    const NR = mobile ? 52 : 70, NT = mobile ? 128 : 180;
    const pos = new Float32Array(NR * NT * 3), col = new Float32Array(NR * NT * 3);
    const cSand = new THREE.Color(0x5f5a40), cGrass = new THREE.Color(0x4f6328), cForest = new THREE.Color(0x3c5226), cRock = new THREE.Color(0x6d6a5c), c = new THREE.Color();
    for (let i = 0; i < NR; i++) {
      const t = i / (NR - 1);
      const s = t < 0.3 ? lerp(0.6, 0.98, t / 0.3) : t < 0.55 ? lerp(0.98, 1.3, (t - 0.3) / 0.25) : lerp(1.3, 40, Math.pow((t - 0.55) / 0.45, 1.9));
      for (let j = 0; j < NT; j++) {
        const th = (j / NT) * Math.PI * 2, rr = s * lakeR(th);
        const x = Math.cos(th) * rr, z = Math.sin(th) * rr, h = heightAt(x, z);
        const k = (i * NT + j) * 3;
        pos[k] = x; pos[k + 1] = h; pos[k + 2] = z;
        const n = fbm(x * 0.05, z * 0.05);
        if (h < 0.15) c.copy(cSand); else if (h < 3) c.copy(cSand).lerp(cGrass, smooth(0.15, 1.2, h)); else c.copy(cGrass).lerp(cForest, smooth(3, 9, h));
        if (h > 30) c.lerp(cRock, smooth(30, 55, h) * 0.6);
        c.multiplyScalar(0.82 + n * 0.36);
        col[k] = c.r; col[k + 1] = c.g; col[k + 2] = c.b;
      }
    }
    const idx = [];
    for (let i = 0; i < NR - 1; i++) for (let j = 0; j < NT; j++) {
      const a = i * NT + j, b = i * NT + ((j + 1) % NT), c2 = (i + 1) * NT + j, d = (i + 1) * NT + ((j + 1) % NT);
      idx.push(a, b, c2, b, d, c2);
    }
    const tg = new THREE.BufferGeometry();
    tg.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    tg.setAttribute('color', new THREE.BufferAttribute(col, 3));
    tg.setIndex(idx); tg.computeVertexNormals();
    scene.add(new THREE.Mesh(tg, new THREE.MeshLambertMaterial({ vertexColors: true })));

    // distant mountains
    const MT = 96, mp = [], mi = [], mc = [];
    const mcol = new THREE.Color(0x56697a);
    for (let j = 0; j <= MT; j++) {
      const th = (j / MT) * Math.PI * 2, R = 2600;
      const top = 120 + fbm(j * 0.21, 3.3) * 380;
      mp.push(Math.cos(th) * R, -20, Math.sin(th) * R, Math.cos(th) * R, top, Math.sin(th) * R);
      mc.push(mcol.r, mcol.g, mcol.b, mcol.r * 1.15, mcol.g * 1.15, mcol.b * 1.15);
      if (j < MT) { const a = j * 2; mi.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
    }
    const mg = new THREE.BufferGeometry();
    mg.setAttribute('position', new THREE.Float32BufferAttribute(mp, 3));
    mg.setAttribute('color', new THREE.Float32BufferAttribute(mc, 3));
    mg.setIndex(mi); mg.computeVertexNormals();
    scene.add(new THREE.Mesh(mg, new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide, fog: true })));

    // cottage spot on the far shore (trees keep clear of it)
    {
      const th = -Math.PI / 2 - 0.3, rr = lakeR(th) + 10;
      const x = Math.cos(th) * rr, z = Math.sin(th) * rr;
      this.cot = { th, x, z, y: heightAt(x, z), rot: Math.atan2(-x, -z) };
    }

    // trees (instanced pines + broadleaf)
    const pinePts = [[0, 1.4], [0.2, 1.4], [2.1, 1.9], [0.85, 3.7], [1.55, 3.7], [0.55, 5.4], [1.0, 5.4], [0, 7.2]].map((p) => new THREE.Vector2(p[0], p[1]));
    const pineGeo = new THREE.LatheGeometry(pinePts, 7);
    this.pineGeo = pineGeo;
    const trunkGeo = new THREE.CylinderGeometry(0.16, 0.26, 2, 5); trunkGeo.translate(0, 1, 0);
    this.trunkGeo = trunkGeo;
    const leafGeo = new THREE.IcosahedronGeometry(2.3, 0); leafGeo.translate(0, 4.2, 0);
    const nP = mobile ? 420 : 820, nL = mobile ? 90 : 180;
    const pines = new THREE.InstancedMesh(pineGeo, new THREE.MeshLambertMaterial({ color: 0xffffff }), nP);
    const leafs = new THREE.InstancedMesh(leafGeo, new THREE.MeshLambertMaterial({ color: 0xffffff, flatShading: true }), nL);
    const trunks = new THREE.InstancedMesh(trunkGeo, new THREE.MeshLambertMaterial({ color: 0x4a3626 }), nP + nL);
    const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), sc = new THREE.Vector3(), p3 = new THREE.Vector3(), yAxis = new THREE.Vector3(0, 1, 0), tc = new THREE.Color();
    let ti = 0;
    const place = (mesh, n, colors, sMin, sMax) => {
      let placed = 0, guard = 0;
      while (placed < n && guard++ < n * 20) {
        const th = Math.random() * Math.PI * 2, s = 1.08 + Math.pow(Math.random(), 1.9) * 5.2, rr = s * lakeR(th);
        const x = Math.cos(th) * rr, z = Math.sin(th) * rr;
        if (Math.hypot(x - this.camPos.x, z - this.camPos.z) < 22) continue;
        if (Math.hypot(x - this.cot.x, z - this.cot.z) < 15) continue;
        const h = heightAt(x, z); if (h < 0.6) continue;
        const k = rand(sMin, sMax) * (1 + Math.min(1.2, (s - 1) * 0.12));
        q.setFromAxisAngle(yAxis, Math.random() * 6.28);
        p3.set(x, h - 0.2, z); sc.set(k * rand(0.85, 1.15), k * rand(0.9, 1.3), k * rand(0.85, 1.15));
        m4.compose(p3, q, sc); mesh.setMatrixAt(placed, m4);
        tc.setHex(colors[Math.floor(Math.random() * colors.length)]).multiplyScalar(rand(0.8, 1.15)); mesh.setColorAt(placed, tc);
        trunks.setMatrixAt(ti++, m4);
        placed++;
      }
      mesh.count = placed;
    };
    place(pines, nP, [0x2f4a26, 0x36522a, 0x28401f, 0x3d5a2c], 1.1, 2.1);
    place(leafs, nL, [0x5d7a2e, 0x6b8434, 0x4f6c2a, 0x7c8d3a], 1.0, 1.7);
    trunks.count = ti;
    [pines, leafs, trunks].forEach((m) => { m.instanceMatrix.needsUpdate = true; if (m.instanceColor) m.instanceColor.needsUpdate = true; scene.add(m); });

    // reeds by the dock
    const nR = mobile ? 160 : 280;
    const reedGeo = new THREE.PlaneGeometry(0.06, 1.4); reedGeo.translate(0, 0.7, 0);
    const reeds = new THREE.InstancedMesh(reedGeo, new THREE.MeshLambertMaterial({ color: 0xffffff, side: THREE.DoubleSide }), nR);
    for (let i = 0; i < nR; i++) {
      const th = Math.PI / 2 + (Math.random() < 0.5 ? -1 : 1) * rand(0.06, 0.55), s = rand(0.97, 1.06), rr = s * lakeR(th);
      const x = Math.cos(th) * rr, z = Math.sin(th) * rr;
      q.setFromEuler(new THREE.Euler(rand(-0.25, 0.25), rand(0, 6.28), rand(-0.25, 0.25)));
      p3.set(x + rand(-1, 1), -0.6, z + rand(-1, 1)); const k = rand(0.8, 1.6); sc.set(1, k, 1);
      m4.compose(p3, q, sc); reeds.setMatrixAt(i, m4);
      tc.setHex([0x6b7a35, 0x7f8a3e, 0x5a6a2c, 0x9a955a][i % 4]); reeds.setColorAt(i, tc);
    }
    reeds.instanceMatrix.needsUpdate = true; reeds.instanceColor.needsUpdate = true;
    scene.add(reeds);

    // dock
    const woodC = [0x6e5238, 0x5f4630, 0x7a5c3f];
    const z0 = this.camPos.z - 1.6, z1 = this.shoreZ + 4, planks = [], posts = [];
    for (let z = z0, i = 0; z < z1; z += 0.3, i++) planks.push([rand(-0.03, 0.03), 0.55, z, 0, woodC[i % 3]]);
    for (let z = z0 + 0.2; z < z1; z += 3) for (const x of [-1.15, 1.15]) posts.push([x, -0.95, z, 0, woodC[1]]);
    scene.add(instanced(new THREE.BoxGeometry(2.4, 0.08, 0.26), planks));
    scene.add(instanced(new THREE.CylinderGeometry(0.1, 0.1, 3.2, 6), posts));
  }

  buildRod() {
    const cam = this.camera;
    const base = new THREE.Group();
    cam.add(base);
    this.rodBase = base;
    const blank = new THREE.MeshStandardMaterial({ color: 0x15181c, roughness: 0.35, metalness: 0.4 });
    const guideMat = new THREE.MeshStandardMaterial({ color: 0xb8bcc2, roughness: 0.3, metalness: 0.8 });
    // handle + reel
    const handle = new THREE.Mesh(new THREE.CylinderGeometry(0.019, 0.023, 0.34, 10), new THREE.MeshStandardMaterial({ color: 0xb08a5a, roughness: 0.9 }));
    handle.position.y = -0.17; base.add(handle);
    const reel = new THREE.Group();
    const spool = new THREE.Mesh(new THREE.CylinderGeometry(0.034, 0.034, 0.05, 16), new THREE.MeshStandardMaterial({ color: 0x2a2d31, roughness: 0.4, metalness: 0.6 }));
    spool.rotation.z = Math.PI / 2;
    const stem = new THREE.Mesh(new THREE.BoxGeometry(0.008, 0.06, 0.012), guideMat); stem.position.set(0, 0.035, 0);
    const crank = new THREE.Group();
    const arm = new THREE.Mesh(new THREE.BoxGeometry(0.006, 0.05, 0.006), guideMat); arm.position.y = 0.025;
    const knob = new THREE.Mesh(new THREE.CylinderGeometry(0.007, 0.007, 0.02, 8), new THREE.MeshStandardMaterial({ color: 0xFF6B1F, roughness: 0.5 }));
    knob.rotation.z = Math.PI / 2; knob.position.set(0.012, 0.05, 0);
    crank.add(arm, knob); crank.position.x = 0.03;
    reel.add(spool, stem, crank);
    reel.position.set(0, -0.08, 0.06); reel.rotation.x = Math.PI / 2;
    base.add(reel);
    this.crank = crank;
    // blank as a bendable chain
    const N = 10, L = 1.95;
    this.segs = [];
    let parent = base;
    for (let i = 0; i < N; i++) {
      const pivot = new THREE.Group();
      if (i > 0) pivot.position.y = L / N;
      const r0 = lerp(0.016, 0.0032, i / N), r1 = lerp(0.016, 0.0032, (i + 1) / N);
      const m = new THREE.Mesh(new THREE.CylinderGeometry(r1, r0, L / N, 6), blank);
      m.position.y = L / N / 2;
      pivot.add(m);
      if (i % 2 === 1) { const g = new THREE.Mesh(new THREE.TorusGeometry(0.008 + 0.006 * (1 - i / N), 0.0015, 4, 10), guideMat); g.position.set(0, L / N, 0.012); g.rotation.y = Math.PI / 2; pivot.add(g); }
      parent.add(pivot);
      this.segs.push(pivot);
      parent = pivot;
    }
    this.tip = new THREE.Object3D(); this.tip.position.y = L / N; parent.add(this.tip);
    // arm + hand
    const sleeve = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.075, 0.7, 10), new THREE.MeshStandardMaterial({ color: 0x3d4552, roughness: 0.95 }));
    sleeve.position.set(0.09, -0.48, 0.2); sleeve.rotation.set(0.7, 0, 0.35);
    const hand = new THREE.Mesh(new THREE.SphereGeometry(0.05, 12, 10), new THREE.MeshStandardMaterial({ color: 0xc6977a, roughness: 0.8 }));
    hand.scale.set(1, 1.5, 1.1); hand.position.set(0.01, -0.2, 0.02);
    base.add(sleeve, hand);
    // line + lure
    this.lineN = 28;
    const lg = new THREE.BufferGeometry();
    lg.setAttribute('position', new THREE.BufferAttribute(new Float32Array(this.lineN * 3), 3));
    this.lineMat = new THREE.LineBasicMaterial({ color: 0xf4f4ee, transparent: true, opacity: 0.75 });
    this.line = new THREE.Line(lg, this.lineMat); this.line.frustumCulled = false;
    this.scene.add(this.line);
    const lure = new THREE.Group();
    const lb = new THREE.Mesh(new THREE.SphereGeometry(0.05, 12, 8), new THREE.MeshStandardMaterial({ color: 0xFF6B1F, roughness: 0.35, metalness: 0.2, emissive: 0x401400 }));
    lb.scale.set(1.1, 0.55, 0.55);
    const lw = new THREE.Mesh(new THREE.SphereGeometry(0.016, 10, 6), new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.3 }));
    lw.position.set(0.035, 0.01, 0);
    lure.add(lb, lw);
    this.lureMesh = lure;
    this.scene.add(lure);
    this.rodPose = 0; this.rodBend = 0.05; this.rodSide = 0;
  }

  buildFx() {
    // ripple rings
    const rg = new THREE.RingGeometry(0.86, 1, 40); rg.rotateX(-Math.PI / 2);
    this.ringPool = [];
    for (let i = 0; i < 48; i++) {
      const m = new THREE.Mesh(rg, new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, depthWrite: false }));
      m.visible = false; m.position.y = 0.04; this.scene.add(m); this.ringPool.push(m);
    }
    // splash particles
    this.pN = 360;
    const pg = new THREE.BufferGeometry();
    this.pPos = new Float32Array(this.pN * 3).fill(-999);
    this.pVel = new Float32Array(this.pN * 3); this.pLife = new Float32Array(this.pN);
    pg.setAttribute('position', new THREE.BufferAttribute(this.pPos, 3));
    this.points = new THREE.Points(pg, new THREE.PointsMaterial({ color: 0xffffff, size: 0.09, transparent: true, opacity: 0.9, depthWrite: false }));
    this.points.frustumCulled = false;
    this.scene.add(this.points);
    this.pNext = 0;
  }


  /* ---------- scenery & life ---------- */
  buildExtras() {
    const scene = this.scene, mobile = this.mobile;
    const lam = (c, o) => new THREE.MeshLambertMaterial(Object.assign({ color: c }, o || {}));
    const add = (parent, geo, mat, x, y, z, rx, ry, rz) => { const m = new THREE.Mesh(geo, mat); m.position.set(x, y, z); m.rotation.set(rx || 0, ry || 0, rz || 0); parent.add(m); return m; };

    // --- red cottage, jetty, boat
    const C = this.cot, cot = new THREE.Group();
    const red = lam(0x93291e), white = lam(0xf1ede4), roofM = lam(0x2f3236, { flatShading: true }), glass = new THREE.MeshStandardMaterial({ color: 0x1d2a33, roughness: 0.15, metalness: 0.6 });
    add(cot, new THREE.BoxGeometry(6.4, 3.2, 4.6), red, 0, 1.6, 0);
    const roofG = new THREE.CylinderGeometry(1, 1, 7, 3, 1); roofG.rotateZ(Math.PI / 2); roofG.rotateX(-Math.PI / 2); roofG.scale(1, 1.2, 3.0);
    add(cot, roofG, roofM, 0, 3.8, 0);
    const trimG = new THREE.BoxGeometry(0.16, 3.25, 0.16);
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) add(cot, trimG, white, sx * 3.2, 1.6, sz * 2.3);
    const winG = new THREE.BoxGeometry(1.0, 0.9, 0.06), frameG = new THREE.BoxGeometry(1.2, 1.1, 0.05);
    for (const wx of [-1.8, 1.8]) { add(cot, frameG, white, wx, 1.9, 2.31); add(cot, winG, glass, wx, 1.9, 2.34); }
    add(cot, new THREE.BoxGeometry(1.1, 2.1, 0.06), white, 0, 1.05, 2.32);
    add(cot, new THREE.BoxGeometry(0.85, 1.85, 0.07), lam(0x6b3a22), 0, 0.98, 2.34);
    add(cot, new THREE.BoxGeometry(0.6, 1.6, 0.6), lam(0x4a4644), 1.8, 4.9, -0.6);
    // flagpole with an orange pennant
    add(cot, new THREE.CylinderGeometry(0.05, 0.06, 7, 6), white, -4.4, 3.5, 2.6);
    const pen = new THREE.Shape(); pen.moveTo(0, 0); pen.lineTo(1.9, -0.35); pen.lineTo(0, -0.7);
    this.pennant = add(cot, new THREE.ShapeGeometry(pen), lam(0xFF6B1F, { side: THREE.DoubleSide }), -4.4, 6.9, 2.6);
    cot.position.set(C.x, C.y - 0.1, C.z); cot.rotation.y = C.rot;
    scene.add(cot);
    // jetty from the cottage into the lake
    const jet = new THREE.Group(), jp = [], jposts = [];
    for (let i = 0; i < 40; i++) jp.push([0, 0.45, 4 + i * 0.3, 0, [0x6e5238, 0x5f4630, 0x7a5c3f][i % 3]]);
    for (let i = 0; i < 5; i++) for (const sx of [-0.75, 0.75]) jposts.push([sx, -0.7, 4.2 + i * 2.8, 0, 0x5f4630]);
    jet.add(instanced(new THREE.BoxGeometry(1.6, 0.07, 0.24), jp), instanced(new THREE.CylinderGeometry(0.08, 0.08, 2.4, 5), jposts));
    jet.position.set(C.x, 0, C.z); jet.rotation.y = C.rot;
    scene.add(jet);
    // rowboat moored beside the jetty
    const hullG = new THREE.BoxGeometry(1.3, 0.5, 3.4, 4, 2, 8), hp = hullG.attributes.position;
    for (let i = 0; i < hp.count; i++) {
      const z = hp.getZ(i), y = hp.getY(i), k = Math.pow(Math.abs(z) / 1.7, 3);
      hp.setX(i, hp.getX(i) * (1 - k * 0.85) * (y < 0 ? 0.7 : 1));
    }
    hullG.computeVertexNormals();
    const boat = new THREE.Group();
    add(boat, hullG, lam(0xe9e4d8));
    add(boat, new THREE.BoxGeometry(1.0, 0.04, 2.2), lam(0x7a5232), 0, 0.22, 0);
    add(boat, new THREE.BoxGeometry(1.1, 0.06, 0.3), lam(0x7a5232), 0, 0.3, 0.3);
    add(boat, new THREE.BoxGeometry(1.32, 0.08, 3.0), lam(0x2a5c8a), 0, 0.1, 0).scale.set(1.01, 1, 0.92);
    const bdir = new THREE.Vector3(Math.sin(C.rot), 0, Math.cos(C.rot)), bside = new THREE.Vector3(Math.cos(C.rot), 0, -Math.sin(C.rot));
    boat.position.set(C.x + bdir.x * 12.5 + bside.x * 1.9, 0.05, C.z + bdir.z * 12.5 + bside.z * 1.9);
    boat.rotation.y = C.rot + 0.15;
    scene.add(boat);
    this.boat = boat;

    // --- island with pines and rocks
    const ig = new THREE.SphereGeometry(1, 24, 10, 0, Math.PI * 2, 0, Math.PI / 2), ip = ig.attributes.position, icol = new Float32Array(ip.count * 3);
    const cS = new THREE.Color(0x857a58), cG = new THREE.Color(0x4f6328), tc = new THREE.Color();
    for (let i = 0; i < ip.count; i++) {
      const x = ip.getX(i), y = ip.getY(i), z = ip.getZ(i), n = 0.85 + fbm(x * 3 + 7, z * 3) * 0.35;
      ip.setXYZ(i, x * n, y, z * n);
      tc.copy(cS).lerp(cG, smooth(0.18, 0.45, y)).multiplyScalar(0.85 + fbm(x * 9, z * 9) * 0.3);
      icol[i * 3] = tc.r; icol[i * 3 + 1] = tc.g; icol[i * 3 + 2] = tc.b;
    }
    ig.setAttribute('color', new THREE.BufferAttribute(icol, 3)); ig.computeVertexNormals();
    const isle = new THREE.Mesh(ig, lam(0xffffff, { vertexColors: true }));
    isle.scale.set(ISLE.r, 2.6, ISLE.r * 0.8); isle.position.set(ISLE.x, -0.75, ISLE.z);
    scene.add(isle);
    const pineM = lam(0x2f4a26), trunkM = lam(0x4a3626), rockG = new THREE.DodecahedronGeometry(1, 0), rockM = lam(0x77736a, { flatShading: true });
    {
      const ip2 = new THREE.InstancedMesh(this.pineGeo, pineM, 8), it2 = new THREE.InstancedMesh(this.trunkGeo, trunkM, 8);
      const mm = new THREE.Matrix4(), qq = new THREE.Quaternion(), pp = new THREE.Vector3(), ss = new THREE.Vector3(), up = new THREE.Vector3(0, 1, 0);
      for (let i = 0; i < 8; i++) {
        const a = rand(0, 6.28), r = rand(0, ISLE.r * 0.55), k = rand(0.9, 1.5);
        const y = -0.75 + 2.6 * Math.sqrt(Math.max(0, 1 - (r / ISLE.r) ** 2)) - 0.2;
        pp.set(ISLE.x + Math.cos(a) * r, y, ISLE.z + Math.sin(a) * r * 0.8); qq.setFromAxisAngle(up, rand(0, 6)); ss.set(k, k * rand(1, 1.3), k);
        mm.compose(pp, qq, ss); ip2.setMatrixAt(i, mm); it2.setMatrixAt(i, mm);
      }
      scene.add(ip2, it2);
    }
    for (let i = 0; i < 6; i++) {
      const a = rand(0, 6.28), r = ISLE.r * rand(0.85, 1.1);
      const m = new THREE.Mesh(rockG, rockM); m.position.set(ISLE.x + Math.cos(a) * r, -0.1, ISLE.z + Math.sin(a) * r * 0.8);
      m.scale.set(rand(0.6, 1.4), rand(0.4, 0.9), rand(0.6, 1.3)); m.rotation.set(rand(0, 3), rand(0, 3), 0); scene.add(m);
    }

    // --- lily pads + flowers
    const padG = new THREE.CircleGeometry(0.42, 12, 0.35, Math.PI * 2 - 0.7); padG.rotateX(-Math.PI / 2);
    const nPad = mobile ? 140 : 240;
    const pads = new THREE.InstancedMesh(padG, lam(0xffffff, { side: THREE.DoubleSide }), nPad);
    const flowerG = new THREE.ConeGeometry(0.13, 0.16, 7, 1, true); flowerG.rotateX(Math.PI);
    const flowers = new THREE.InstancedMesh(flowerG, lam(0xffffff, { side: THREE.DoubleSide }), 40);
    const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), sc = new THREE.Vector3(), p3 = new THREE.Vector3(), yAxis = new THREE.Vector3(0, 1, 0);
    const beds = [Math.PI / 2 - 0.32, Math.PI / 2 + 0.38, Math.PI / 2 + 1.3, -0.2, Math.PI + 0.4, -Math.PI / 2 + 0.6];
    let fi = 0, pi = 0;
    for (let i = 0; i < nPad; i++) {
      const b = beds[i % beds.length], th = b + rand(-0.12, 0.12), rr = lakeR(th) * rand(0.86, 0.95);
      p3.set(Math.cos(th) * rr + rand(-2, 2), 0.035, Math.sin(th) * rr + rand(-2, 2));
      if (Math.hypot(p3.x - this.camPos.x, p3.z - this.camPos.z) < 5) continue;
      q.setFromAxisAngle(yAxis, rand(0, 6.28)); const k = rand(0.7, 1.4); sc.set(k, 1, k);
      m4.compose(p3, q, sc); pads.setMatrixAt(pi, m4);
      tc.setHex([0x3f6a2a, 0x4d7a30, 0x355c25, 0x5a8236][i % 4]); pads.setColorAt(pi, tc); pi++;
      if (fi < 40 && Math.random() < 0.18) {
        p3.y = 0.1; q.identity(); sc.setScalar(rand(0.8, 1.2)); m4.compose(p3, q, sc); flowers.setMatrixAt(fi, m4);
        tc.setHex(Math.random() < 0.5 ? 0xf6f1f4 : 0xf2a7c1); flowers.setColorAt(fi, tc); fi++;
      }
    }
    flowers.count = fi; pads.count = pi;
    [pads, flowers].forEach((m) => { m.instanceMatrix.needsUpdate = true; if (m.instanceColor) m.instanceColor.needsUpdate = true; scene.add(m); });

    // --- dock props
    const tackle = add(scene, new THREE.BoxGeometry(0.5, 0.26, 0.28), lam(0xFF6B1F), 0.75, 0.72, this.camPos.z - 1.2, 0, 0.3, 0);
    add(tackle, new THREE.BoxGeometry(0.2, 0.05, 0.05), lam(0x222222), 0, 0.15, 0);
    add(scene, new THREE.CylinderGeometry(0.17, 0.14, 0.34, 12, 1, true), lam(0x8d9399, { side: THREE.DoubleSide }), -0.8, 0.76, this.camPos.z - 1.0);

    // --- ducks
    this.ducks = [];
    const dBody = new THREE.SphereGeometry(0.22, 10, 8), dHead = new THREE.SphereGeometry(0.1, 8, 6), dBeak = new THREE.ConeGeometry(0.035, 0.12, 6); dBeak.rotateZ(-Math.PI / 2);
    const brown = lam(0x6e5034), green = lam(0x1f5a32), yellow = lam(0xe0a22a), beige = lam(0x9a7d5a);
    for (let i = 0; i < 4; i++) {
      const g = new THREE.Group();
      const male = i % 2 === 0;
      const b = add(g, dBody, male ? brown : beige, 0, 0.06, 0); b.scale.set(1.5, 0.75, 0.95);
      add(g, dHead, male ? green : beige, 0.27, 0.22, 0);
      add(g, dBeak, yellow, 0.4, 0.2, 0);
      const x = rand(-16, 16), z = rand(4, 28);
      g.position.set(x, 0, z);
      scene.add(g);
      this.ducks.push({ g, x, z, hx: x, hz: z, speed: 0.5, flee: 0, wake: 0 });
    }

    // --- gulls
    this.gulls = [];
    const wingG = new THREE.PlaneGeometry(0.9, 0.3); wingG.translate(0.45, 0, 0); wingG.rotateX(-Math.PI / 2);
    const gullM = lam(0xf4f4f2, { side: THREE.DoubleSide }), tipM = lam(0x3a3a3a, { side: THREE.DoubleSide });
    for (let i = 0; i < 3; i++) {
      const g = new THREE.Group();
      const body = add(g, new THREE.SphereGeometry(0.14, 8, 6), gullM); body.scale.set(1, 0.8, 2.4);
      const L = new THREE.Group(), R = new THREE.Group();
      add(L, wingG, gullM); add(R, wingG, gullM).scale.x = -1;
      add(L, new THREE.PlaneGeometry(0.2, 0.2).rotateX(-Math.PI / 2).translate(0.9, 0.001, 0), tipM);
      add(R, new THREE.PlaneGeometry(0.2, 0.2).rotateX(-Math.PI / 2).translate(-0.9, 0.001, 0), tipM);
      g.add(L, R);
      scene.add(g);
      this.gulls.push({ g, L, R, cx: rand(-20, 15), cz: rand(-30, 5), r: rand(18, 40), h: rand(14, 26), a: rand(0, 6.28), v: rand(0.12, 0.2) * (Math.random() < 0.5 ? -1 : 1), ph: rand(0, 6) });
    }

    // ambient fish jumps
    this.jumps = []; this.jumpMesh = {};
    this.clouds = scene.children.filter((o) => o.isSprite);
  }

  ambientJump(f) {
    if (this.jumps.some((j) => j.kind === f.kind)) return;
    let m = this.jumpMesh[f.kind];
    if (!m) { m = this.fishMesh(Object.assign({}, f, { len: (f.sp.len[0] + f.sp.len[1]) / 2 })); this.jumpMesh[f.kind] = m; this.scene.add(m); }
    m.visible = true; m.scale.setScalar(f.len / ((f.sp.len[0] + f.sp.len[1]) / 2));
    this.jumps.push({ kind: f.kind, m, x: f.x, z: f.z, t: 0, dur: 0.7 + f.len * 0.3, h: 0.5 + f.len * 0.9, yaw: rand(0, 6.28) });
    this.splash(f.x, 0.05, f.z, 8, 1.2);
  }

  updateLife(dt) {
    const t = this.time;
    if (this.pennant) this.pennant.rotation.y = Math.sin(t * 2.2) * 0.35;
    if (this.boat) { this.boat.position.y = 0.05 + Math.sin(t * 1.3) * 0.04; this.boat.rotation.z = Math.sin(t * 1.1) * 0.04; this.boat.rotation.x = Math.sin(t * 0.9) * 0.025; }
    for (const c of this.clouds || []) { c.position.x += dt * 4; if (c.position.x > 2600) c.position.x = -2600; }
    for (const d of this.ducks || []) {
      if (Math.hypot(d.hx - d.x, d.hz - d.z) < 1 || Math.random() < dt * 0.04) {
        const a = rand(0, 6.28), nx = d.x + Math.cos(a) * rand(4, 12), nz = d.z + Math.sin(a) * rand(4, 12);
        if (insideLake(nx, nz, 0.85) && Math.hypot(nx - this.camPos.x, nz - this.camPos.z) > 8) { d.hx = nx; d.hz = nz; }
      }
      d.flee = Math.max(0, d.flee - dt);
      const sp = d.flee > 0 ? 2.6 : 0.45, a = Math.atan2(d.hz - d.z, d.hx - d.x);
      d.x += Math.cos(a) * sp * dt; d.z += Math.sin(a) * sp * dt;
      d.g.position.set(d.x, 0.02 + Math.sin(t * 2 + d.hx) * 0.015, d.z);
      d.g.rotation.y = -a;
      d.wake -= dt;
      if (d.wake <= 0) { d.wake = d.flee > 0 ? 0.15 : 0.7; this.ring(d.x, d.z, d.flee > 0 ? 0.6 : 0.35, 0.35, 1.1); }
    }
    for (const g of this.gulls || []) {
      g.a += g.v * dt;
      const x = g.cx + Math.cos(g.a) * g.r, z = g.cz + Math.sin(g.a) * g.r;
      g.g.position.set(x, g.h + Math.sin(g.a * 3) * 2, z);
      g.g.rotation.set(0, -g.a + (g.v > 0 ? 0 : Math.PI), Math.sin(g.a * 2) * 0.2);
      const flap = Math.sin(t * 7 + g.ph) > 0.2 ? Math.sin(t * 9 + g.ph) * 0.55 : 0.08;
      g.L.rotation.z = flap; g.R.rotation.z = -flap;
    }
    for (let i = this.jumps.length - 1; i >= 0; i--) {
      const j = this.jumps[i]; j.t += dt;
      const k = j.t / j.dur;
      j.m.position.set(j.x + Math.cos(j.yaw) * k * 0.8, Math.sin(k * Math.PI) * j.h - 0.1, j.z + Math.sin(j.yaw) * k * 0.8);
      j.m.rotation.set(0, -j.yaw, (0.5 - k) * 1.8);
      j.m.userData.tail.rotation.y = Math.sin(t * 20) * 0.5;
      if (k >= 1) { j.m.visible = false; this.splash(j.m.position.x, 0.05, j.m.position.z, 10, 1.3); this.ring(j.m.position.x, j.m.position.z, 1, 0.6, 1.3); this.jumps.splice(i, 1); }
    }
  }

  /* ---------- fish ---------- */
  populate() {
    this.fish = [];
    this.spots = [];
    for (let i = 0; i < 5; i++) {
      const th = rand(-Math.PI * 0.95, -Math.PI * 0.05) + (Math.random() < 0.3 ? Math.PI : 0), s = rand(0.45, 0.8);
      this.spots.push({ x: Math.cos(th) * lakeR(th) * s, z: Math.sin(th) * lakeR(th) * s });
    }
    for (let i = 0; i < 22; i++) this.fish.push(this.makeFish());
  }

  makeFish() {
    const kind = pick(SPECIES), sp = SPECIES[kind];
    let t = Math.pow(Math.random(), 2.1);
    const trophy = Math.random() < 0.05;
    if (trophy) t = rand(0.85, 1);
    let x, z, guard = 0;
    do {
      if (Math.random() < 0.7) { const s = this.spots[Math.floor(Math.random() * this.spots.length)]; x = s.x + rand(-9, 9); z = s.z + rand(-9, 9); }
      else { const th = rand(0, Math.PI * 2), r = lakeR(th) * rand(0.15, 0.85); x = Math.cos(th) * r; z = Math.sin(th) * r; }
    } while ((!insideLake(x, z, 0.86) || Math.hypot(x - this.camPos.x, z - this.camPos.z) < 10) && guard++ < 50);
    return {
      kind, sp, trophy, weight: lerp(sp.min, sp.max, t), len: lerp(sp.len[0], sp.len[1], t),
      x, z, hx: x, hz: z, heading: rand(0, 6.28), state: 'roam', cool: 0, interest: 0, riseT: rand(1, 12), think: rand(0, 0.5)
    };
  }

  fishMesh(f) {
    const sp = f.sp, L = f.len, H = L * sp.girth, W = H * 0.5;
    const g = new THREE.SphereGeometry(0.5, 22, 14);
    const p = g.attributes.position, col = new Float32Array(p.count * 3);
    const cb = new THREE.Color(sp.back), cl = new THREE.Color(sp.belly), c = new THREE.Color(), dark = new THREE.Color(0x15190e), pink = new THREE.Color(0xd77b8b), lite = new THREE.Color(0xe8e3b4);
    for (let i = 0; i < p.count; i++) {
      let x = p.getX(i), y = p.getY(i), z = p.getZ(i);
      const taper = x < 0 ? 1 + x * 1.15 : 1 - x * x * 0.6;
      y *= taper; z *= taper;
      const v = clamp(0.5 + y * 1.6, 0, 1);
      c.copy(cl).lerp(cb, v);
      if (sp.pattern === 'bars' && Math.sin(x * 34) > 0.45 && y > -0.12) c.lerp(dark, 0.55);
      if (sp.pattern === 'band' && Math.abs(y) < 0.07 && x < 0.38) c.lerp(dark, 0.5);
      if (sp.pattern === 'trout') { if (Math.abs(y) < 0.08) c.lerp(pink, 0.6); if (hash(i, 3) > 0.86 && y > -0.1) c.lerp(dark, 0.7); }
      if (sp.pattern === 'pike' && hash(i, 7) > 0.72 && Math.abs(y) < 0.3) c.lerp(lite, 0.7);
      p.setXYZ(i, x * L, y * H, z * W);
      col[i * 3] = c.r; col[i * 3 + 1] = c.g; col[i * 3 + 2] = c.b;
    }
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    g.computeVertexNormals();
    const grp = new THREE.Group();
    const body = new THREE.Mesh(g, new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.42, metalness: 0.15 }));
    grp.add(body);
    const finMat = new THREE.MeshStandardMaterial({ color: sp.fin, roughness: 0.6, side: THREE.DoubleSide, transparent: true, opacity: 0.92 });
    const ts = new THREE.Shape(); ts.moveTo(0, 0); ts.lineTo(-L * 0.2, H * 0.6); ts.lineTo(-L * 0.14, 0); ts.lineTo(-L * 0.2, -H * 0.6); ts.lineTo(0, 0);
    const tail = new THREE.Mesh(new THREE.ShapeGeometry(ts), finMat); tail.position.x = -L * 0.43;
    const tailPivot = new THREE.Group(); tailPivot.position.x = -L * 0.02; tailPivot.add(tail); grp.add(tailPivot);
    const ds = new THREE.Shape(); ds.moveTo(L * 0.12, 0); ds.lineTo(-L * 0.04, H * 0.42); ds.lineTo(-L * 0.22, 0);
    const dorsal = new THREE.Mesh(new THREE.ShapeGeometry(ds), finMat); dorsal.position.y = H * 0.4; grp.add(dorsal);
    const eyeG = new THREE.SphereGeometry(Math.max(0.006, L * 0.028), 8, 6), eyeM = new THREE.MeshStandardMaterial({ color: 0x0b0b0b, roughness: 0.2 });
    for (const s of [-1, 1]) { const e = new THREE.Mesh(eyeG, eyeM); e.position.set(L * 0.36, H * 0.12, s * W * 0.4); grp.add(e); }
    grp.userData.tail = tailPivot;
    return grp;
  }

  /* ---------- input ---------- */
  bind() {
    const cv = this.cv, act = this.q('[data-g-act]');
    let look = null;
    cv.addEventListener('pointerdown', (e) => {
      look = { id: e.pointerId, x: e.clientX, y: e.clientY };
      try { cv.setPointerCapture(e.pointerId); } catch (err) {}
    });
    cv.addEventListener('pointermove', (e) => {
      if (!look || e.pointerId !== look.id) return;
      const k = this.mobile ? 0.006 : 0.004;
      this.yaw = clamp(this.yaw - (e.clientX - look.x) * k, -0.95, 0.95);
      this.pitch = clamp(this.pitch - (e.clientY - look.y) * k * 0.5, -0.32, 0.12);
      look.x = e.clientX; look.y = e.clientY;
      if (this.state === 'play') this.root.classList.add('has-looked');
    });
    const endLook = () => { look = null; };
    cv.addEventListener('pointerup', endLook); cv.addEventListener('pointercancel', endLook);

    act.addEventListener('pointerdown', (e) => { e.preventDefault(); try { act.setPointerCapture(e.pointerId); } catch (err) {} this.actDown(); });
    act.addEventListener('pointerup', (e) => { e.preventDefault(); this.actUp(); });
    act.addEventListener('pointercancel', () => this.actUp());
    act.addEventListener('contextmenu', (e) => e.preventDefault());

    this.keys = {};
    window.addEventListener('keydown', (e) => {
      if (this.state !== 'play' || !this.visible) return;
      const t = e.target; if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.code === 'Space') { e.preventDefault(); if (!e.repeat) this.actDown(); }
      if (['ArrowLeft', 'ArrowRight', 'KeyA', 'KeyD'].includes(e.code)) { e.preventDefault(); this.keys[e.code] = true; }
    });
    window.addEventListener('keyup', (e) => {
      if (e.code === 'Space') { if (this.state === 'play') this.actUp(); }
      this.keys[e.code] = false;
    });
    this.root.querySelectorAll('[data-g-start]').forEach((b) => b.addEventListener('click', () => this.start()));
    const copy = this.q('[data-g-copy]');
    if (copy) copy.addEventListener('click', () => { try { navigator.clipboard.writeText(this.cfg.code); copy.textContent = 'Copied'; } catch (e) {} });
  }

  actDown() {
    if (this.state !== 'play' || this.trophy) return;
    if (this.bite) { this.strike(); return; }
    if (this.lure.state === 'idle') { this.charging = true; this.powerT = 0; this.power = 0; return; }
    if (this.lure.state === 'water' || this.fight) this.holding = true;
  }

  actUp() {
    this.holding = false;
    if (this.charging) { this.charging = false; this.cast(this.power); }
  }

  /* ---------- flow ---------- */
  start() {
    if (this.state === 'loading') return;
    this.state = 'play';
    this.time = 0; this.score = 0; this.count = 0; this.biggest = null;
    this.lastHud = {};
    this.endFight(); this.clearTrophy();
    this.bite = null; this.follower = null; this.hintedStyle = false; this.hintedReel = false;
    this.lure.state = 'idle';
    this.populate();
    this.q('[data-g-intro]').hidden = true;
    this.q('[data-g-over]').hidden = true;
    this.q('[data-g-catch]').hidden = true;
    this.root.classList.add('is-playing');
    this.toast(this.mobile ? 'Drag to look. Watch for rings — then hold CAST.' : 'Drag to look. Watch for rings — then hold CAST (Space).', 3400);
  }

  end() {
    this.state = 'over';
    this.holding = false; this.charging = false; this.bite = null;
    this.endFight(); this.clearTrophy();
    this.lure.state = 'idle';
    this.root.classList.remove('is-playing');
    const score = Math.round(this.score * 10) / 10;
    if (score > this.best) { this.best = score; store('sg-lake-best', String(score)); }
    this.q('[data-g-final]').textContent = score.toFixed(1) + ' lb';
    this.q('[data-g-final-count]').textContent = this.count + ' fish';
    this.q('[data-g-final-big]').textContent = this.biggest ? this.biggest.name + ' · ' + this.biggest.w.toFixed(1) + ' lb' : '—';
    this.q('[data-g-final-best]').textContent = this.best.toFixed(1) + ' lb';
    const rw = this.q('[data-g-reward]'); if (rw) rw.hidden = !(this.cfg.code && score >= this.cfg.threshold);
    const miss = this.q('[data-g-reward-miss]'); if (miss) miss.hidden = !(this.cfg.code && score < this.cfg.threshold);
    this.q('[data-g-over]').hidden = false;
  }

  toast(msg, ms) {
    const el = this.q('[data-g-toast]');
    el.textContent = msg; el.classList.add('is-on');
    clearTimeout(this.toastT);
    this.toastT = setTimeout(() => el.classList.remove('is-on'), ms || 1600);
  }

  /* ---------- casting & lure ---------- */
  camForward(out) { return out.set(-Math.sin(this.yaw), 0, -Math.cos(this.yaw)); }

  cast(power) {
    const L = this.lure;
    const fwd = this.camForward(this.tmp);
    let d = 7 + power * 43;
    const err = (1 - Math.min(1, power * 1.15)) * 0.04 + 0.015;
    const yawE = this.yaw + rand(-err, err);
    let x, z;
    for (let k = 0; k < 40; k++) {
      x = this.camPos.x - Math.sin(yawE) * d; z = this.camPos.z - Math.cos(yawE) * d;
      if (insideLake(x, z, 0.94)) break;
      d -= 1;
    }
    if (d < 5) { this.toast('Too close to shore. Aim out over the water.', 1500); return; }
    this.tip.getWorldPosition(L.from);
    L.to.set(x, 0.03, z);
    L.t = 0; L.dur = 0.55 + d / 45; L.state = 'flying'; L.dist = d;
    this.rodPose = 1; // whip
    this.root.classList.remove('show-power');
  }

  landLure() {
    const L = this.lure;
    L.state = 'water'; L.pos.copy(L.to); L.moving = false; L.moveT = 0; L.pauseT = 0;
    if (!this.hintedReel && this.state === 'play') { this.hintedReel = true; this.toast('Hold REEL to work the lure. Short taps = twitches.', 2600); }
    this.ring(L.pos.x, L.pos.z, 0.9, 0.7, 1.4);
    this.splash(L.pos.x, 0.02, L.pos.z, 10, 1.4);
    // ducks
    for (const d of this.ducks || []) {
      const dd = Math.hypot(d.x - L.pos.x, d.z - L.pos.z);
      if (dd < 5) {
        d.flee = 3; const a = Math.atan2(d.z - L.pos.z, d.x - L.pos.x); d.hx = d.x + Math.cos(a) * 14; d.hz = d.z + Math.sin(a) * 14;
        if (this.state === 'play') this.toast(dd < 1.3 ? 'Whoa — you almost hooked a duck!' : 'Quack! You spooked the ducks.', 1500);
      }
    }
    // spook fish right under the splash
    for (const f of this.fish) {
      const d = Math.hypot(f.x - L.pos.x, f.z - L.pos.z);
      if (f.state === 'roam' && d < 1.5) { f.cool = 4; this.flee(f, L.pos.x, L.pos.z); }
    }
  }

  flee(f, x, z) { const a = Math.atan2(f.z - z, f.x - x); f.hx = clamp(f.x + Math.cos(a) * 14, -80, 80); f.hz = clamp(f.z + Math.sin(a) * 14, -80, 80); }

  retrieveDone() {
    const L = this.lure;
    if (this.follower) {
      if (Math.random() < 0.5) this.toast('It followed all the way in… then turned.', 1800);
      this.follower.state = 'roam'; this.follower.cool = 5; this.follower = null;
    }
    L.state = 'idle';
  }

  /* ---------- bite / strike ---------- */
  triggerBite(f) {
    this.bite = { f, t: 0, win: f.sp.style === 'fast' ? 1.0 : 1.2 };
    this.follower = null;
    this.splash(this.lure.pos.x, 0.05, this.lure.pos.z, 16 + Math.round(f.len * 20), 2.2);
    this.ring(this.lure.pos.x, this.lure.pos.z, 1 + f.len, 0.85, 1.2);
    this.rodKick = 1;
    if (navigator.vibrate) { try { navigator.vibrate(90); } catch (e) {} }
    this.toast('STRIKE!', 700);
  }

  strike() {
    const b = this.bite; this.bite = null;
    const rt = b.t;
    let chance = rt < 0.5 ? 0.96 : clamp(0.96 - (rt - 0.5) * 0.8, 0.5, 0.96);
    if (b.f.trophy) chance -= 0.05;
    this.rodKick = 1.4;
    if (Math.random() < chance) this.hook(b.f);
    else { b.f.state = 'roam'; b.f.cool = 8; this.flee(b.f, this.lure.pos.x, this.lure.pos.z); this.toast(rt > 0.6 ? 'Late hookset — it spat the lure.' : 'Missed it! Pure bad luck.', 1700); }
  }

  hook(f) {
    f.state = 'fight';
    const dx = this.lure.pos.x - this.camPos.x, dz = this.lure.pos.z - this.camPos.z;
    const d = Math.hypot(dx, dz);
    this.fight = {
      f, d, maxD: d + 6, ang: Math.atan2(dx, -dz), tension: 0.45, stamina: 1, burst: 0, burstT: rand(0.6, 1.4),
      over: 0, slack: 0, pull: 0.5, wob: rand(0, 6), jump: null, mesh: null
    };
    this.q('[data-g-gauge]').hidden = false;
    this.toast(f.trophy ? 'HEAVY ONE! Keep the needle in the green.' : 'FISH ON! Keep the needle in the green.', 2000);
  }

  endFight() {
    if (this.fight && this.fight.mesh) { this.scene.remove(this.fight.mesh); }
    this.fight = null;
    const g = this.q('[data-g-gauge]'); if (g) { g.hidden = true; g.classList.remove('is-let'); }
  }

  loseFish(msg) {
    const f = this.fight.f;
    this.endFight();
    f.state = 'roam'; f.cool = 10; f.x = this.lure.pos.x; f.z = this.lure.pos.z; this.flee(f, this.camPos.x, this.camPos.z);
    this.lure.state = 'idle';
    this.toast(msg, 2000);
  }

  landFish() {
    const F = this.fight, f = F.fish || F.f, w = Math.round(f.weight * 10) / 10;
    this.endFight();
    this.fish.splice(this.fish.indexOf(f), 1);
    setTimeout(() => { if (this.state === 'play') this.fish.push(this.makeFish()); }, 4000);
    this.score += w; this.count++;
    if (!this.biggest || w > this.biggest.w) this.biggest = { name: f.sp.name, w };
    this.lure.state = 'idle';
    // trophy shot in front of the camera
    const m = this.fishMesh(f);
    const s = Math.min(1, 0.52 / f.len);
    m.scale.setScalar(s);
    m.position.set(0, -0.1, -0.78); m.rotation.set(0.1, -0.35, 0.05);
    this.camera.add(m);
    this.trophy = { m, t: 0 };
    const card = this.q('[data-g-catch]');
    this.q('[data-g-catch-name]').textContent = (f.trophy ? 'Trophy ' : '') + f.sp.name;
    this.q('[data-g-catch-w]').textContent = '+' + w.toFixed(1) + ' lb';
    card.classList.toggle('is-trophy', !!f.trophy);
    card.hidden = false; card.classList.remove('is-in'); void card.offsetWidth; card.classList.add('is-in');
    this.splash(this.camPos.x, 0.1, this.camPos.z - 3, 20, 2);
  }

  clearTrophy() {
    if (this.trophy) { this.camera.remove(this.trophy.m); this.trophy = null; }
    const card = this.q('[data-g-catch]'); if (card) card.hidden = true;
  }

  /* ---------- fx ---------- */
  ring(x, z, size, a0, life) {
    const m = this.ringPool.find((r) => !r.visible); if (!m) return;
    m.visible = true; m.position.set(x, 0.04, z); m.scale.setScalar(0.1);
    m.userData = { t: 0, life: life || 1.6, size: size || 1, a0: a0 || 0.6 };
    m.material.opacity = a0 || 0.6;
  }

  splash(x, y, z, n, power) {
    for (let i = 0; i < n; i++) {
      const k = this.pNext; this.pNext = (this.pNext + 1) % this.pN;
      const a = Math.random() * 6.28, v = rand(0.4, 1.2) * power;
      this.pPos[k * 3] = x; this.pPos[k * 3 + 1] = y; this.pPos[k * 3 + 2] = z;
      this.pVel[k * 3] = Math.cos(a) * v * 0.6; this.pVel[k * 3 + 1] = rand(1.2, 2.6) * power; this.pVel[k * 3 + 2] = Math.sin(a) * v * 0.6;
      this.pLife[k] = rand(0.5, 0.9);
    }
  }

  /* ---------- per frame ---------- */
  resize() {
    const r = this.cv.getBoundingClientRect();
    const w = Math.max(200, Math.round(r.width)), h = Math.max(200, Math.round(r.height));
    if (w === this.W && h === this.H) return;
    this.W = w; this.H = h;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.fov = w / h < 1 ? 70 : 56;
    this.camera.updateProjectionMatrix();
    // rod placement: portrait vs landscape
    if (w / h < 1) { this.rodBase.position.set(0.2, -0.34, -0.5); }
    else { this.rodBase.position.set(0.34, -0.3, -0.55); }
  }

  update(dt) {
    const playing = this.state === 'play';
    this.time += dt;
    if (playing) {
      this.left = this.cfg.round - this.time;
      if (this.left <= 0) this.end();
    }
    // look
    if (this.keys) {
      if (this.keys.ArrowLeft || this.keys.KeyA) this.yaw = clamp(this.yaw + dt * 0.9, -0.95, 0.95);
      if (this.keys.ArrowRight || this.keys.KeyD) this.yaw = clamp(this.yaw - dt * 0.9, -0.95, 0.95);
    }
    const bob = Math.sin(this.time * 0.8) * 0.006;
    this.camera.position.set(this.camPos.x, this.camPos.y + bob, this.camPos.z);
    this.camera.rotation.set(this.pitch + Math.sin(this.time * 0.6) * 0.004, this.yaw, 0);

    this.water.material.uniforms.time.value += dt * 0.45;

    // charge power (ping-pong)
    if (this.charging) {
      this.powerT += dt;
      const ph = (this.powerT / 1.25) % 2;
      this.power = ph < 1 ? ph : 2 - ph;
      this.rodPose = -0.6 * this.power;
    }

    // lure
    const L = this.lure;
    if (L.state === 'flying') {
      L.t += dt / L.dur;
      const k = Math.min(1, L.t);
      L.pos.lerpVectors(L.from, L.to, k);
      L.pos.y += Math.sin(k * Math.PI) * (3 + L.dist * 0.16);
      if (L.t >= 1) this.landLure();
    } else if (L.state === 'water' && !this.fight) {
      const wasMoving = L.moving;
      L.moving = this.holding && playing && !this.bite;
      if (L.moving) {
        L.moveT = wasMoving ? L.moveT + dt : dt; L.pauseT = 0;
        const dx = this.camPos.x - L.pos.x, dz = this.camPos.z - L.pos.z, dd = Math.hypot(dx, dz);
        const v = 3.1;
        L.pos.x += (dx / dd) * v * dt; L.pos.z += (dz / dd) * v * dt;
        if ((this.wakeT = (this.wakeT || 0) - dt) <= 0) { this.wakeT = 0.22; this.ring(L.pos.x, L.pos.z, 0.35, 0.35, 0.9); }
        if (dd < 6) this.retrieveDone();
      } else { L.pauseT = wasMoving ? dt : L.pauseT + dt; }
      L.pos.y = 0.03 + Math.sin(this.time * 3) * 0.008;
    } else if (L.state === 'idle') {
      this.tip.getWorldPosition(this.tmp);
      L.pos.set(this.tmp.x, this.tmp.y - 0.35 + Math.sin(this.time * 2) * 0.01, this.tmp.z);
    }

    // fish AI
    if (playing) this.updateFish(dt);
    if (this.bite) {
      this.bite.t += dt;
      if (this.bite.t > this.bite.win) {
        const f = this.bite.f; this.bite = null;
        f.state = 'roam'; f.cool = 8; this.flee(f, L.pos.x, L.pos.z);
        this.toast('Too slow — it let go.', 1500);
      }
    }
    if (this.fight && playing) this.updateFight(dt);

    // trophy
    if (this.trophy) {
      this.trophy.t += dt;
      const m = this.trophy.m;
      m.rotation.y = -0.35 + Math.sin(this.trophy.t * 1.6) * 0.25;
      m.userData.tail.rotation.y = Math.sin(this.trophy.t * 14) * 0.35;
      if (this.trophy.t > 2.4) this.clearTrophy();
    }

    this.updateLife(dt);
    this.updateRod(dt);
    this.updateLine();
    this.updateFx(dt);
    this.updateHud();
  }

  updateFish(dt) {
    const L = this.lure;
    for (const f of this.fish) {
      if (f.state === 'fight') continue;
      f.cool = Math.max(0, f.cool - dt);
      // rises — the visual tells
      f.riseT -= dt;
      if (f.riseT <= 0 && f.state === 'roam') {
        f.riseT = rand(5, 12) / (0.6 + f.sp.aggr * 0.6);
        this.ring(f.x, f.z, 0.5 + f.len * 1.4, 0.6, 1.8);
        if (f.kind !== 'perch' && Math.random() < 0.22) this.ambientJump(f);
        else if (Math.random() < 0.5) this.splash(f.x, 0.02, f.z, 4 + Math.round(f.len * 6), 0.8);
      }
      if (f.state === 'roam') {
        if (Math.hypot(f.hx - f.x, f.hz - f.z) < 1.5 || Math.random() < dt * 0.05) {
          const a = rand(0, 6.28), r = rand(2, 10);
          const nx = f.x + Math.cos(a) * r, nz = f.z + Math.sin(a) * r;
          if (insideLake(nx, nz, 0.86)) { f.hx = nx; f.hz = nz; }
        }
        const a = Math.atan2(f.hz - f.z, f.hx - f.x);
        f.x += Math.cos(a) * 0.7 * dt; f.z += Math.sin(a) * 0.7 * dt;
        // notice the lure
        f.think -= dt;
        if (f.think <= 0) {
          f.think = rand(0.35, 0.6);
          if (L.state === 'water' && !this.follower && !this.bite && !this.fight && f.cool <= 0) {
            const d = Math.hypot(f.x - L.pos.x, f.z - L.pos.z);
            if (d < 10) {
              const chance = f.sp.aggr * (L.moving ? 0.8 : 0.15) * (1 - d / 13);
              if (Math.random() < chance) { f.state = 'follow'; f.interest = 0.45; this.follower = f; f.followT = 0; }
            }
          }
        }
      } else if (f.state === 'follow') {
        if (L.state !== 'water' || this.follower !== f) { f.state = 'roam'; continue; }
        f.followT += dt;
        // trail the lure
        const dx = L.pos.x - f.x, dz = L.pos.z - f.z, d = Math.hypot(dx, dz);
        const want = d > 1.2 ? Math.min(3.4, d * 1.6) : 0.4;
        f.x += (dx / (d || 1)) * want * dt; f.z += (dz / (d || 1)) * want * dt;
        if (L.moving && (this.followWake = (this.followWake || 0) - dt) <= 0) { this.followWake = 0.18; this.ring(f.x, f.z, 0.25 + f.len * 0.9, 0.42, 0.8); }
        // retrieve technique decides
        const st = f.sp.style;
        let di = 0;
        if (st === 'twitch') {
          if (L.moving) di = L.moveT < 0.8 ? 0.32 : 0.1;
          else di = L.pauseT > 0.2 && L.pauseT < 1.8 ? 0.8 : L.pauseT >= 2.5 ? -0.4 : 0;
        } else if (st === 'steady') {
          if (L.moving) di = L.moveT > 0.9 ? 0.55 : 0.42;
          else di = L.pauseT > 0.7 ? -0.35 : 0;
        } else {
          if (L.moving) di = L.moveT > 0.5 ? 0.5 : 0.4;
          else di = L.pauseT > 0.5 ? -0.45 : 0;
        }
        f.interest += di * dt;
        if (Math.random() < dt * 0.12 * f.sp.aggr) f.interest += 0.4; // lucky smash
        if (f.followT > 1.2 && !this.hintedStyle && Math.random() < dt * 0.6) {
          this.hintedStyle = true; this.toast('Something\'s following… ' + STYLE_TIP[st], 2600);
        }
        if (f.interest >= 1 && d < 2.4) { f.state = 'bite'; this.triggerBite(f); }
        else if (f.interest <= 0) {
          this.follower = null; f.state = 'roam'; f.cool = 7; this.flee(f, L.pos.x, L.pos.z);
          if (Math.random() < 0.6) this.toast('It lost interest. Change your rhythm.', 1600);
        }
      }
    }
  }

  updateFight(dt) {
    const F = this.fight, f = F.f, wN = (f.weight - f.sp.min) / (f.sp.max - f.sp.min);
    const gauge = this.q('[data-g-gauge]');
    // runs
    F.burstT -= dt;
    if (F.burstT <= 0 && !F.jump) {
      F.burst = rand(0.6, 1.1) * (0.55 + F.stamina * 0.6); F.burstT = rand(1.6, 3.6) - wN * 0.6;
      if (Math.random() < f.sp.jump * 0.55 && F.d > 5) { F.jump = { t: 0, dur: 0.95, held: 0 }; this.splash(this.lure.pos.x, 0.05, this.lure.pos.z, 18, 2); this.toast('JUMP! Let go!', 900); }
    }
    F.burst = Math.max(0, F.burst - dt);
    let pull = f.sp.power * (0.35 + 0.65 * F.stamina) * (F.burst > 0 ? 1.6 : 1) * (0.88 + 0.12 * Math.sin(this.time * 7 + F.wob));
    pull *= 0.7 + wN * 0.35;
    F.pull = pull;
    const reeling = this.holding;
    const target = reeling ? 0.18 + pull * 0.66 : pull * 0.3;
    F.tension += (target - F.tension) * Math.min(1, dt * (reeling ? 3.2 : 4.5));
    if (reeling) F.d -= 3.4 * Math.max(0.1, 1.12 - pull) * dt;
    else F.d += pull * 1.9 * dt;
    F.d = Math.min(F.d, F.maxD);
    F.stamina = Math.max(0, F.stamina - dt * (0.035 + (reeling ? F.tension * 0.07 : 0.015)) / (0.6 + wN * 0.9));
    F.ang = clamp(F.ang + Math.sin(this.time * 0.8 + F.wob) * dt * 0.28 * (0.4 + pull), -0.9, 0.9);
    let x = this.camPos.x + Math.sin(F.ang) * F.d, z = this.camPos.z - Math.cos(F.ang) * F.d;
    let guard = 0;
    while (!insideLake(x, z, 0.92) && guard++ < 30) { F.d -= 0.6; x = this.camPos.x + Math.sin(F.ang) * F.d; z = this.camPos.z - Math.cos(F.ang) * F.d; }
    f.x = x; f.z = z;
    this.lure.pos.set(x, 0.03, z);
    if (Math.random() < dt * (F.burst > 0 ? 9 : 2.5)) this.ring(x, z, 0.4 + f.len, 0.45, 0.9);
    if (F.burst > 0 && Math.random() < dt * 6) this.splash(x, 0.03, z, 3, 1);

    // show the fish near the surface when close, or when it jumps
    const show = F.d < 13 || F.jump;
    if (show && !F.mesh) { F.mesh = this.fishMesh(f); this.scene.add(F.mesh); }
    if (F.mesh) {
      F.mesh.visible = !!show;
      const away = Math.atan2(x - this.camPos.x, -(z - this.camPos.z));
      F.mesh.position.set(x, -0.04 + Math.sin(this.time * 9) * 0.02, z);
      F.mesh.rotation.set(0, -away + Math.PI / 2 + Math.sin(this.time * 12) * 0.25, Math.sin(this.time * 7) * 0.15);
      F.mesh.userData.tail.rotation.y = Math.sin(this.time * 18) * 0.5;
    }
    if (F.jump) {
      const J = F.jump; J.t += dt; if (reeling) J.held += dt;
      const k = J.t / J.dur, hgt = Math.sin(k * Math.PI) * (0.6 + f.len * 1.3);
      if (F.mesh) { F.mesh.position.y = hgt; F.mesh.rotation.z = (0.5 - k) * 1.6; }
      if (J.t >= J.dur) {
        this.splash(x, 0.05, z, 22, 2.2); this.ring(x, z, 1.2 + f.len, 0.8, 1.4);
        F.jump = null;
        if (J.held > 0.35 && Math.random() < 0.35) return this.loseFish('It shook the hook on the jump. Let go when it jumps!');
      }
    }
    const letGo = F.burst > 0.35 || F.jump || F.tension > 0.9;
    gauge.classList.toggle('is-let', !!letGo);

    if (F.tension > 1) { F.over += dt; if (F.over > 0.55) return this.loseFish('SNAP! The line broke.'); }
    else F.over = Math.max(0, F.over - dt);
    if (F.tension < 0.1) { F.slack += dt; if (F.slack > 2.6) return this.loseFish('Slack line — it threw the hook.'); }
    else F.slack = 0;
    if (F.d < 2.6) this.landFish();
  }

  updateRod(dt) {
    // cast whip / charge pose
    if (!this.charging) this.rodPose += (0 - this.rodPose) * Math.min(1, dt * (this.rodPose > 0 ? 7 : 4));
    this.rodKick = Math.max(0, (this.rodKick || 0) - dt * 4);
    let bend = 0.06;
    if (this.fight) bend = 0.15 + clamp(this.fight.tension, 0, 1.15) * 1.25;
    else if (this.lure.state === 'water') bend = this.lure.moving ? 0.22 : 0.1;
    bend += this.rodKick * 0.7;
    this.rodBend += (bend - this.rodBend) * Math.min(1, dt * 8);
    // side pull toward the lure
    let side = 0;
    if (this.lure.state === 'water') {
      const a = Math.atan2(this.lure.pos.x - this.camPos.x, -(this.lure.pos.z - this.camPos.z));
      side = clamp(a + this.yaw, -0.6, 0.6);
    }
    this.rodSide += (side - this.rodSide) * Math.min(1, dt * 5);
    const N = this.segs.length;
    this.rodBase.rotation.set(-1.08 - this.rodPose * 0.9 + (this.fight ? 0.25 : 0), 0.1, -0.12);
    for (let i = 0; i < N; i++) {
      const w = (i + 1) / ((N * (N + 1)) / 2);
      this.segs[i].rotation.x = -this.rodBend * w * N * 0.1 * (i / N + 0.4);
      this.segs[i].rotation.z = -this.rodSide * this.rodBend * w * 1.4;
    }
    if (this.holding && (this.lure.state === 'water' || this.fight)) this.crank.rotation.x += dt * 14;
  }

  updateLine() {
    const tip = this.tip.getWorldPosition(this.tmp2), L = this.lure.pos;
    this.lureMesh.position.copy(L);
    this.lureMesh.visible = !this.fight || !(this.fight.mesh && this.fight.mesh.visible);
    if (this.lure.state !== 'idle') this.lureMesh.rotation.y = Math.atan2(tip.x - L.x, tip.z - L.z) + Math.PI / 2;
    const arr = this.line.geometry.attributes.position.array, N = this.lineN;
    const tension = this.fight ? clamp(this.fight.tension, 0, 1) : this.lure.state === 'water' ? (this.lure.moving ? 0.6 : 0.15) : 0.8;
    const span = tip.distanceTo(L);
    const sag = (1 - tension) * Math.min(3, span * 0.08) + 0.02;
    for (let i = 0; i < N; i++) {
      const t = i / (N - 1);
      let y = lerp(tip.y, L.y, t) - sag * 4 * t * (1 - t);
      if (this.lure.state !== 'flying' && this.lure.state !== 'idle') y = Math.max(y, 0.02);
      arr[i * 3] = lerp(tip.x, L.x, t); arr[i * 3 + 1] = y; arr[i * 3 + 2] = lerp(tip.z, L.z, t);
    }
    this.line.geometry.attributes.position.needsUpdate = true;
    const tc = this.fight ? this.fight.tension : 0;
    this.lineMat.color.setHex(tc > 0.9 ? 0xff5040 : tc > 0.75 ? 0xffa060 : 0xf4f4ee);
  }

  updateFx(dt) {
    for (const m of this.ringPool) {
      if (!m.visible) continue;
      const u = m.userData; u.t += dt;
      const k = u.t / u.life;
      if (k >= 1) { m.visible = false; continue; }
      m.scale.setScalar(0.1 + u.size * (1 - Math.pow(1 - k, 2)) * 1.6);
      m.material.opacity = u.a0 * (1 - k);
    }
    const P = this.pPos, V = this.pVel;
    for (let i = 0; i < this.pN; i++) {
      if (this.pLife[i] <= 0) continue;
      this.pLife[i] -= dt;
      V[i * 3 + 1] -= 9.8 * dt;
      P[i * 3] += V[i * 3] * dt; P[i * 3 + 1] += V[i * 3 + 1] * dt; P[i * 3 + 2] += V[i * 3 + 2] * dt;
      if (this.pLife[i] <= 0 || P[i * 3 + 1] < -0.05) { this.pLife[i] = 0; P[i * 3 + 1] = -999; }
    }
    this.points.geometry.attributes.position.needsUpdate = true;
  }

  updateHud() {
    const h = this.lastHud;
    const set = (sel, key, val) => { if (h[key] !== val) { h[key] = val; const el = this.q(sel); if (el) el.textContent = val; } };
    if (this.state === 'play') {
      set('[data-g-time]', 'time', fmtTime(this.left));
      set('[data-g-score]', 'score', this.score.toFixed(1) + ' lb');
      set('[data-g-count]', 'count', String(this.count));
    }
    // action button
    let label = 'Cast', mode = 'cast';
    if (this.trophy) { label = 'Nice!'; mode = 'wait'; }
    else if (this.bite) { label = 'Strike!'; mode = 'strike'; }
    else if (this.fight) { label = 'Reel'; mode = 'reel'; }
    else if (this.lure.state === 'flying') { label = '…'; mode = 'wait'; }
    else if (this.lure.state === 'water') { label = 'Reel'; mode = 'reel'; }
    if (h.mode !== mode) { h.mode = mode; this.root.dataset.mode = mode; }
    set('[data-g-act-label]', 'act', label);
    // power ring
    const pr = Math.round((this.charging ? this.power : 0) * 100);
    if (h.pow !== pr) { h.pow = pr; const c = this.q('[data-g-power]'); if (c) c.style.strokeDashoffset = String(100 - pr); this.root.classList.toggle('is-charging', this.charging); }
    // distance
    let dist = '';
    if (this.fight) dist = this.fight.d.toFixed(1) + ' m';
    else if (this.lure.state === 'water') dist = Math.hypot(this.lure.pos.x - this.camPos.x, this.lure.pos.z - this.camPos.z).toFixed(1) + ' m';
    set('[data-g-dist]', 'dist', dist);
    this.root.classList.toggle('has-dist', !!dist);
    // tension needle
    if (this.fight) {
      const t = clamp(this.fight.tension / 1.1, 0, 1);
      const deg = Math.round(-90 + t * 180);
      if (h.needle !== deg) { h.needle = deg; const n = this.q('[data-g-needle]'); if (n) n.setAttribute('transform', 'rotate(' + deg + ' 60 60)'); }
    }
  }
}

function boot(root) { if (!root.sgLake) root.sgLake = new LakeGame(root); }
document.querySelectorAll('[data-sg-lake]').forEach(boot);
document.addEventListener('shopify:section:load', (e) => e.target.querySelectorAll('[data-sg-lake]').forEach(boot));
